import React, { useCallback } from "react";
import { Box, Stack, Typography, Button, LinearProgress } from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import { FiArrowLeft, FiArrowRight, FiCheck } from "react-icons/fi";
import { showSuccessToast as ToastSuccess, showErrorToast as ToastError } from "../../../../utils/Toaster/AppToast";
import { createRecurringTemplate, updateRecurringTemplate } from "../../../../services/recurringTemplate.service";
import dayjs from "dayjs";
import useRecurringForm from "../../hooks/useRecurringForm";
import StepTaskDetails from "./StepTaskDetails";
import StepRecurrence  from "./StepRecurrence";
import StepReview      from "./StepReview";

const T = {
  primary: "#0D9488", primaryDark: "#0F766E", primaryLight: "#F0FDFA",
  border: "#E2E8F0", text: "#0F172A", textSub: "#475569", textMuted: "#94A3B8",
  surface: "#FFFFFF", bg: "#F8FAFC",
  shadow: { md: "0 4px 16px rgba(15,23,42,0.08)" },
};

const STEPS = ["Task Details", "Recurrence", "Review"];

const USER_TZ = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";

const stripHtml = (html) => {
  if (!html) return "";
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  return (tmp.textContent || tmp.innerText || "").trim();
};

// Maps a source task's fields into a complete form-initial-values object.
// Recurrence fields stay at their defaults — user fills those in step 2.
const taskToFormValues = (task) => ({
  title:                task.title       || "",
  description:          stripHtml(task.description),
  priority:             task.priority    || 1,
  defaultStatus:        [1, 2, 3].includes(task.status) ? task.status : 1,
  privateAssignees:     !!task.privateAssignees,
  assignees:            Array.isArray(task.assignees) && task.assignees.length
    ? task.assignees.map((a) => ({ _id: String(a.user ?? a._id ?? a), label: a.name || a.userName || "" }))
    : task.assignee && task.assigneeName
    ? [{ _id: String(task.assignee), label: task.assigneeName }]
    : [],
  support:              task.support && task.supportName
    ? { _id: String(task.support), label: task.supportName }
    : null,
  acknowledgedBy:       task.acknowledgedBy && task.acknowledgedByName
    ? { _id: String(task.acknowledgedBy), label: task.acknowledgedByName }
    : null,
  recurrenceType:       "weekly",
  recurrenceInterval:   1,
  recurrenceDaysOfWeek: [],
  recurrenceDayOfMonth: 1,
  recurrenceNthWeekday: { n: 1, d: 1 },
  monthlyMode:          "fixed",
  recurrenceMonth:      0,
  recurringStartDate:   dayjs().format("YYYY-MM-DD"),
  recurrenceEndType:    "never",
  recurrenceEndDate:    null,
  recurrenceEndAfter:   null,
  recurrenceTimezone:   USER_TZ,
  generationWindow:     14,
});

const toFormValues = (t) => ({
  title:                t.title || "",
  description:          t.description || "",
  priority:             t.priority || 1,
  defaultStatus:        t.defaultStatus || 1,
  privateAssignees:     !!t.privateAssignees,
  assignees:            Array.isArray(t.assignees) && t.assignees.length
    ? t.assignees.map((a) => ({
        _id: String(a.user?._id || a.user || a._id || a),
        label: a.user?.userName || a.user?.name || a.userName || "",
      }))
    : t.assignee
    ? [{ _id: String(t.assignee._id || t.assignee), label: t.assignee.userName || "" }]
    : [],
  support:              t.support
    ? { _id: t.support._id || t.support, label: t.support.userName || "" }
    : null,
  acknowledgedBy:       t.acknowledgedBy
    ? { _id: t.acknowledgedBy._id || t.acknowledgedBy, label: t.acknowledgedBy.userName || "" }
    : null,
  recurrenceType:       t.recurrenceType || "weekly",
  recurrenceInterval:   t.recurrenceInterval || 1,
  recurrenceDaysOfWeek: t.recurrenceDaysOfWeek || [],
  recurrenceDayOfMonth: t.recurrenceDayOfMonth || 1,
  recurrenceNthWeekday: t.recurrenceNthWeekday || { n: 1, d: 1 },
  monthlyMode:          t.monthlyMode || (t.recurrenceNthWeekday?.n != null ? "nth" : "fixed"),
  recurrenceMonth:      t.recurrenceMonth ?? 0,
  recurringStartDate:   t.recurringStartDate
    ? dayjs(t.recurringStartDate).format("YYYY-MM-DD")
    : dayjs().format("YYYY-MM-DD"),
  recurrenceEndType:    t.recurrenceEndType || "never",
  recurrenceEndDate:    t.recurrenceEndDate || null,
  recurrenceEndAfter:   t.recurrenceEndAfter || null,
  recurrenceTimezone:   t.recurrenceTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
  generationWindow:     t.generationWindow ?? 14,
});

const RecurringTemplateForm = ({ onSuccess, onCancel, template = null, prefillData = null }) => {
  const isEdit = !!template;
  const form = useRecurringForm(
    isEdit       ? toFormValues(template)
    : prefillData ? taskToFormValues(prefillData)
    : null
  );
  const { step, values, errors, submitting, set, setMany, setSubmitting, nextStep, prevStep, buildPayload, isFirstStep, isLastStep } = form;

  const handleAssigneesChange = useCallback((options) => {
    // Seed Support AND Acknowledged By from the FIRST assignee, only on the
    // 0 -> 1 transition and only while each is still empty — matching the Add
    // Task modal. Keying off `options.length === 1` instead also re-seeded when
    // REMOVING an assignee back down to one, which nobody asked for.
    const wasEmpty = (form.values.assignees || []).length === 0;
    set("assignees", options);
    if (wasEmpty && options.length > 0) {
      if (!form.values.support) set("support", options[0]);
      if (!form.values.acknowledgedBy) set("acknowledgedBy", options[0]);
    }
  }, [set, form.values.assignees, form.values.support, form.values.acknowledgedBy]);

  const handleSubmit = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const payload = buildPayload();
      const res = isEdit
        ? await updateRecurringTemplate(template._id, payload)
        : await createRecurringTemplate(payload);
      if (res?.error === false) {
        // On create the server names the task it spawned immediately — show it.
        ToastSuccess(res?.message || (isEdit ? "Template updated successfully" : "Recurring task created successfully"));
        onSuccess?.(res.data);
        form.reset();
      } else {
        ToastError(res?.message || (isEdit ? "Failed to update template" : "Failed to create template"));
      }
    } catch {
      ToastError("Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 600, mx: "auto" }}>
      {/* Progress bar */}
      <LinearProgress
        variant="determinate"
        value={((step + 1) / STEPS.length) * 100}
        sx={{
          mb: 3, height: 4, borderRadius: 2,
          bgcolor: T.border,
          "& .MuiLinearProgress-bar": { bgcolor: T.primary, borderRadius: 2 },
        }}
      />

      {/* Step indicators */}
      <Stack direction="row" alignItems="center" justifyContent="center" gap={0} sx={{ mb: 3 }}>
        {STEPS.map((label, i) => (
          <React.Fragment key={i}>
            <Stack alignItems="center" gap={0.5}>
              <Box
                sx={{
                  width: 32, height: 32, borderRadius: "50%",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  bgcolor: i < step ? T.primary : i === step ? T.primary : T.border,
                  color:   i <= step ? "#fff" : T.textMuted,
                  fontWeight: 700, fontSize: "13px",
                  transition: "all 0.2s",
                }}
              >
                {i < step ? <FiCheck size={14} /> : i + 1}
              </Box>
              <Typography sx={{ fontSize: "11px", fontWeight: i === step ? 700 : 500, color: i === step ? T.primary : T.textMuted }}>
                {label}
              </Typography>
            </Stack>
            {i < STEPS.length - 1 && (
              <Box sx={{ flex: 1, height: 2, bgcolor: i < step ? T.primary : T.border, mx: 1, mb: 2.5, transition: "background 0.2s" }} />
            )}
          </React.Fragment>
        ))}
      </Stack>

      {/* Step content */}
      <Box
        sx={{
          p: 3, borderRadius: "16px",
          border: `1px solid ${T.border}`,
          bgcolor: T.surface,
          boxShadow: T.shadow.md,
          mb: 3,
          minHeight: 340,
        }}
      >
        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.18 }}
          >
            {step === 0 && <StepTaskDetails values={values} set={set} errors={errors} supportValue={values.support} onSupportChange={(v) => set("support", v)} onAssigneesChange={handleAssigneesChange} acknowledgedByValue={values.acknowledgedBy} onAcknowledgedByChange={(v) => set("acknowledgedBy", v)} />}
            {step === 1 && <StepRecurrence  values={values} set={set} setMany={setMany} errors={errors} />}
            {step === 2 && <StepReview      values={values} />}
          </motion.div>
        </AnimatePresence>
      </Box>

      {/* Navigation */}
      <Stack direction="row" justifyContent="space-between">
        <Button
          variant="outlined"
          onClick={isFirstStep ? onCancel : prevStep}
          startIcon={isFirstStep ? null : <FiArrowLeft size={14} />}
          sx={{ borderColor: T.border, color: T.textSub, fontWeight: 600, "&:hover": { borderColor: T.textSub } }}
        >
          {isFirstStep ? "Cancel" : "Back"}
        </Button>

        <Button
          variant="contained"
          onClick={isLastStep ? handleSubmit : nextStep}
          disabled={submitting}
          endIcon={isLastStep ? <FiCheck size={14} /> : <FiArrowRight size={14} />}
          sx={{
            bgcolor: T.primary, fontWeight: 700,
            "&:hover": { bgcolor: T.primaryDark },
            "&:disabled": { bgcolor: T.border },
          }}
        >
          {isLastStep
            ? submitting
              ? (isEdit ? "Updating…" : "Creating…")
              : (isEdit ? "Update Template" : "Create Template")
            : "Next"}
        </Button>
      </Stack>
    </Box>
  );
};

export default RecurringTemplateForm;
